import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { productsAPI, categoriesAPI, wishlistAPI, searchAPI, savedSearchesAPI, requestsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { getEffectivePrice } from '../utils/pricing';
import AnimatedAddToCartButton from './AnimatedAddToCartButton';
import AnimatedViewButton from './AnimatedViewButton';
import '../assets/css/styles.css';
import '../assets/css/shop.css';
import '../assets/css/modern-product-card.css';

// ── 3D Tilt Card ──────────────────────────────────────────────────────────────
const TiltCard = ({ children, className, onClick }) => {
  const ref = useRef(null);
  const [rot, setRot] = useState({ x: 0, y: 0 });
  const [glare, setGlare] = useState({ x: 50, y: 50, on: false });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setRot({ x: (py - 0.5) * -12, y: (px - 0.5) * 12 });
    setGlare({ x: px * 100, y: py * 100, on: true });
  };

  const onLeave = () => {
    setRot({ x: 0, y: 0 });
    setGlare(g => ({ ...g, on: false }));
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ transformStyle: 'preserve-3d' }}
      animate={{ rotateX: rot.x, rotateY: rot.y }}
      transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.5 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      layout
    >
      {children}
      {glare.on && (
        <div
          className="shop-card__glare"
          style={{ background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,0.13) 0%, transparent 62%)` }}
        />
      )}
    </motion.div>
  );
};

// ── Condition Badge (holographic) ────────────────────────────────────────────
const conditionMeta = {
  new:          { label: 'Brand New',    cls: 'badge-new' },
  uk_used:      { label: 'UK Used',      cls: 'badge-uk' },
  nigeria_used: { label: 'Nigerian',     cls: 'badge-ng' },
  good_deal:    { label: 'Good Deal',    cls: 'badge-deal' },
  swap_deal:    { label: 'Swap',         cls: 'badge-swap' },
  for_sale:     { label: 'For Sale',     cls: 'badge-sale' },
  refurbished:  { label: 'Refurb',       cls: 'badge-refurb' },
};

const ConditionBadge = ({ condition }) => {
  if (!condition) return null;
  const meta = conditionMeta[condition] || {
    label: condition.replace(/_/g, ' '),
    cls: 'badge-generic',
  };
  return (
    <span className={`shop-card__badge ${meta.cls}`}>
      <i className="fas fa-certificate" />
      {meta.label}
    </span>
  );
};

// ── Digital Price Tag ─────────────────────────────────────────────────────────
const PriceTag = ({ price, sellerPrice, isSeller }) => {
  const fmt = (v) => Number(v).toLocaleString('en-NG');
  return (
    <div className="shop-price-wrap">
      <div className="shop-price-tag">
        <span className="shop-price-curr">₦</span>
        <span className="shop-price-val">{fmt(price)}</span>
      </div>
      {isSeller && sellerPrice && (
        <div className="shop-wholesale-chip">
          <span>WS</span>
          <span className="shop-ws-val">₦{fmt(sellerPrice)}</span>
        </div>
      )}
    </div>
  );
};

// ── Skeleton Card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="shop-skeleton">
    <div className="shop-skeleton__img" />
    <div className="shop-skeleton__body">
      <div className="shop-skeleton__line" style={{ width: '70%' }} />
      <div className="shop-skeleton__line" style={{ width: '45%' }} />
      <div className="shop-skeleton__line" style={{ width: '55%' }} />
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const Shop = ({ darkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { showCustomToast } = useNotifications();
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [memories, setMemories] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedMemories, setSelectedMemories] = useState([]);
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 5000000 });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('featured');
  const [actualPriceRange, setActualPriceRange] = useState({ min: 0, max: 5000000 });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const itemsPerPage = 12;
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    deviceTypes: true,
    price: true,
    brands: true,
    memory: true,
    savedSearches: true,
  });
  const [wishlistItems, setWishlistItems] = useState([]);
  const [compareItems, setCompareItems] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [omniOpen, setOmniOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const omniInputRef = useRef(null);

  // Flash deals
  const [flashDeals, setFlashDeals] = useState([]);
  const [loadingFlash, setLoadingFlash] = useState(false);
  const [flashNow, setFlashNow] = useState(Date.now());

  // Product requests
  const [publicRequests, setPublicRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [wantToBuyMode, setWantToBuyMode] = useState(false);
  const [requestForm, setRequestForm] = useState({ title: '', description: '', budget: '', category: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestResponses, setRequestResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [respondForm, setRespondForm] = useState({ product_url: '', message: '' });
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [fulfillingRequest, setFulfillingRequest] = useState(false);

  const isSeller = user?.user_type === 'seller' || user?.userType === 'seller';
  const isReseller = user?.is_reseller || user?.isReseller;

  // Official platform category slugs — must match backend models.CategorySlug*
  // constants exactly, since the navbar's Shop dropdown links directly to
  // /shop?category=<slug> using these same values.
  // The three condition-backed categories don't need a real category_slug on
  // the product — they're derivable from the existing `condition` field.
  const conditionForCategorySlug = {
    'brand-new': 'new',
    'uk-used': 'uk_used',
    'nigerian-used': 'nigeria_used',
  };
  // For Sale / Swap Deal / Good Deal are derived from existing boolean flags
  // rather than category_slug — same reasoning as conditionForCategorySlug
  // above. For Sale is defined as the cash-only complement of Swap Deal:
  // swap_available === false. Good Deal mirrors the admin's own "Good Deal"
  // toggle, which is actually the product's is_featured flag under the hood.
  const booleanFlagForCategorySlug = {
    'for-sale': (p) => !p.swap_available,
    'swap-deal': (p) => !!p.swap_available,
    'good-deal': (p) => !!p.is_featured,
  };
  const fallbackCategories = [
    { value: 'brand-new',     label: 'Brand New',     id: 'brand-new' },
    { value: 'uk-used',       label: 'UK Used',       id: 'uk-used' },
    { value: 'good-deal',     label: 'Good Deal',     id: 'good-deal' },
    { value: 'nigerian-used', label: 'Nigerian Used', id: 'nigerian-used' },
    { value: 'swap-deal',     label: 'Swap Deal',     id: 'swap-deal' },
    { value: 'for-sale',      label: 'For Sale',      id: 'for-sale' },
    { value: 'random-items',  label: 'Random Items',  id: 'random-items' },
  ];


  // ── Extract storage size from product name/title/specs ───────────────────────
  const extractStorage = (product) => {
    const toGB = (val, unit) => {
      const n = parseInt(val);
      if (unit.toUpperCase() === 'TB') return n * 1024;
      if (unit.toUpperCase() === 'MB') return n / 1024;
      return n;
    };

    // 1. Check structured specs first (explicit storage field, not RAM)
    if (product.specifications) {
      const s = product.specifications;
      const specStorage = s.storage || s.Storage || s.capacity || s.Capacity;
      if (specStorage) {
        const m = String(specStorage).match(/(\d+)\s*(GB|TB|MB)/i);
        if (m) return `${m[1]} ${m[2].toUpperCase()}`;
      }
    }
    // 2. Parse from name + title + description
    const text = `${product.title || ''} ${product.name || ''} ${product.description || ''}`;
    // Strip explicit RAM mentions (e.g. "12GB RAM", "8gb ram") to avoid confusion
    const withoutRam = text.replace(/\d+\s*gb\s*ram/gi, '').replace(/\d+\s*mb\s*ram/gi, '');
    // Find ALL size mentions, then pick the LARGEST — storage is always bigger than RAM
    const matches = [...withoutRam.matchAll(/\b(\d+)\s*(GB|TB|MB)\b/gi)];
    if (matches.length === 0) return null;
    const best = matches.reduce((a, b) => toGB(a[1], a[2]) >= toGB(b[1], b[2]) ? a : b);
    return `${best[1]} ${best[2].toUpperCase()}`;
  };

  // ── Data Fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoriesResponse, wishlistResponse] = await Promise.all([
          productsAPI.getAll(isSeller ? { include_seller_price: true } : {}),
          categoriesAPI.getAll(),
          wishlistAPI.getWishlist().catch(() => ({ data: [] })),
        ]);

        if (wishlistResponse?.data) {
          const wishlistData = wishlistResponse.data.items || wishlistResponse.data || [];
          setWishlistItems(wishlistData.map(item => item.product_id || item.product?._id || item.product?.id));
        }

        if (productsResponse?.data) {
          const productsData = productsResponse.data;
          const activeProducts = productsData.filter(p => p.is_active !== false && p.deleted !== true);
          const mappedProducts = activeProducts.map(product => ({
            ...product,
            stock: product.quantity !== undefined ? product.quantity : (product.stock ?? 0),
            name: product.title || product.name || 'Product',
            image: product.images?.length > 0
              ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)
              : (product.image || 'https://via.placeholder.com/300x300?text=No+Image'),
          }));

          setProducts(mappedProducts);
          setFilteredProducts(mappedProducts);
          setTotalProducts(mappedProducts.length);
          setTotalPages(Math.ceil(mappedProducts.length / itemsPerPage));

          if (productsData.length > 0) {
            const prices = productsData.map(p => p.price || 0);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(Math.max(...prices), 5000000);
            setActualPriceRange({ min: minPrice, max: maxPrice });
            setPriceRange({ min: minPrice, max: maxPrice });
          }

          const uniqueBrands = [...new Set(mappedProducts.map(p => p.brand).filter(Boolean))].sort();
          setBrands(uniqueBrands.length > 0 ? uniqueBrands : ['Apple', 'Samsung', 'Google', 'Xiaomi']);

          const memorySet = new Set();
          mappedProducts.forEach(product => {
            const storage = extractStorage(product);
            if (storage) memorySet.add(storage);
          });
          const getSize = (str) => {
            const m = str.match(/(\d+)\s*(GB|TB|MB)/i);
            if (!m) return 0;
            return m[2].toUpperCase() === 'TB' ? parseInt(m[1]) * 1024 : m[2].toUpperCase() === 'MB' ? parseInt(m[1]) / 1024 : parseInt(m[1]);
          };
          const uniqueMemories = Array.from(memorySet).sort((a, b) => getSize(a) - getSize(b));
          setMemories(uniqueMemories);

          if (categoriesResponse?.data?.length > 0) {
            // Exclude platform categories (Random Items, and any future ones like
            // For Sale) that live in the same shared `categories` collection but
            // aren't real device types — they'd otherwise show up as a confusing
            // extra checkbox next to Phones/Laptops/Gaming in the Device Type filter.
            const platformCategorySlugs = ['brand-new', 'uk-used', 'good-deal', 'nigerian-used', 'swap-deal', 'for-sale', 'want-to-buy', 'random-items'];
            const cats = categoriesResponse.data.filter(cat => cat.name?.trim() && !platformCategorySlugs.includes(cat.slug));
            setDeviceTypes(cats.length > 0 ? cats : [
              { id: 'smartphones', name: 'Smartphones' },
              { id: 'laptops', name: 'Laptops' },
              { id: 'tablets', name: 'Tablets' },
              { id: 'wearables', name: 'Wearables' },
              { id: 'audio-video', name: 'Audio & Video' },
              { id: 'gaming', name: 'Gaming' },
              { id: 'cameras', name: 'Cameras' },
              { id: 'accessories', name: 'Accessories' },
            ]);
          } else {
            setDeviceTypes([
              { id: 'smartphones', name: 'Smartphones' },
              { id: 'laptops', name: 'Laptops' },
              { id: 'tablets', name: 'Tablets' },
              { id: 'wearables', name: 'Wearables' },
              { id: 'audio-video', name: 'Audio & Video' },
              { id: 'gaming', name: 'Gaming' },
              { id: 'cameras', name: 'Cameras' },
              { id: 'accessories', name: 'Accessories' },
            ]);
          }

          // Category options are always the fixed official platform-category
          // slugs (matches backend models.CategorySlug* / the navbar's Shop
          // dropdown links) rather than discovered from live product data —
          // in practice every product's own `category_slug` field currently
          // holds its *device-type* category (e.g. "phones-tablets"), denormalised
          // at creation time from the single category picker sellers get today.
          // There's no seller-facing way yet to tag a listing as Good Deal / Swap
          // Deal / For Sale / Random Items, so those filters correctly show zero
          // results until that's added; Brand New / UK Used / Nigerian Used work
          // today because the filter logic below also matches on `condition`.
          setCategories(fallbackCategories);
        }

        if (!productsResponse?.data?.length) {
          setCategories(categoriesResponse?.data || fallbackCategories);
        }
      } catch {
        // Show nothing rather than invented stock. This used to fall back to a
        // hardcoded list of eight products with real-looking prices, ratings
        // and stock counts — shoppers could open and try to buy items that
        // don't exist. The empty state below is the honest outcome.
        setProducts([]);
        setFilteredProducts([]);
        setCategories(fallbackCategories);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isSeller]);

  useEffect(() => {
    const fetchSavedSearches = async () => {
      try {
        const response = await savedSearchesAPI.getSavedSearches();
        if (response?.data) setSavedSearches(response.data);
      } catch {}
    };
    fetchSavedSearches();
  }, []);

  // Real products the admin marked down (Admin -> Products -> Flash Deal) —
  // NOT the sitewide promo campaigns under Admin -> Marketing -> Sales
  // Campaigns, which have no product attached and can't be shown as a
  // product card.
  useEffect(() => {
    const fetchFlash = async () => {
      try {
        setLoadingFlash(true);
        const r = await productsAPI.getFlashDeals();
        const data = r?.data || r;
        const raw = Array.isArray(data) ? data : (data?.products || []);
        setFlashDeals(raw);
      } catch {} finally { setLoadingFlash(false); }
    };
    fetchFlash();
  }, []);

  useEffect(() => {
    if (!flashDeals.length) return;
    const id = setInterval(() => setFlashNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [flashDeals]);

  useEffect(() => {
    // Buyer requests are personal — the backend now only allows sellers/admins
    // to browse the full feed, so buyers just fetch their own via getMyRequests.
    const fetchRequests = async () => {
      try {
        const [pub, my] = await Promise.all([
          isSeller ? requestsAPI.getAllRequests({ limit: 50 }) : Promise.resolve({ data: [] }),
          user ? requestsAPI.getMyRequests() : Promise.resolve({ data: [] }),
        ]);
        if (pub?.data) setPublicRequests(Array.isArray(pub.data) ? pub.data : pub.data.requests || []);
        if (my?.data) setMyRequests(Array.isArray(my.data) ? my.data : my.data.requests || []);
      } catch {}
    };
    fetchRequests();
  }, [user, isSeller]);

  // Applies the navbar's Shop dropdown links (/shop?category=<slug> and
  // /shop?type=<slug>) to the actual product filters. `category` maps 1:1 to
  // product.category_slug (same official slugs the backend uses). `type` maps
  // to a real device-type Category document, matched by its `slug` field —
  // the navbar's device-type slugs (phones/tablets/audio/headphones) don't
  // exactly match the backend's category slugs, so a small alias table bridges
  // the gap; unmapped/unmatched slugs correctly show zero results rather than
  // silently showing everything.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    const typeParam = params.get('type');

    setWantToBuyMode(categoryParam === 'want-to-buy');

    if (categoryParam && categoryParam !== 'want-to-buy') {
      setSelectedCategories([categoryParam]);
    } else if (!categoryParam) {
      setSelectedCategories([]);
    }

    if (typeParam) {
      const deviceTypeAliases = {
        phones: ['phones-tablets', 'smartphones', 'phones'],
        tablets: ['phones-tablets', 'tablets'],
        laptops: ['laptops-computers', 'laptops'],
        audio: ['audio-video', 'audio'],
        headphones: ['audio-video', 'headphones'],
        gaming: ['gaming'],
        accessories: ['accessories'],
      };
      const candidates = deviceTypeAliases[typeParam] || [typeParam];
      const match = deviceTypes.find(dt => {
        const dtSlug = (dt.slug || '').toLowerCase();
        const dtId = String(dt.id || dt._id || '').toLowerCase();
        return candidates.includes(dtSlug) || candidates.includes(dtId);
      });
      setSelectedDeviceTypes(match ? [match] : []);
    } else {
      setSelectedDeviceTypes([]);
    }
  }, [location.search, deviceTypes]);

  useEffect(() => {
    if (location.state?.searchQuery) setSearchQuery(location.state.searchQuery);
    if (location.state?.filters) {
      const f = location.state.filters;
      if (f.categories) setSelectedCategories(f.categories);
      if (f.brands) setSelectedBrands(f.brands);
      if (f.memories) setSelectedMemories(f.memories);
      if (f.deviceTypes) setSelectedDeviceTypes(f.deviceTypes);
      if (f.priceRange) setPriceRange(f.priceRange);
      if (f.sortBy) setSortBy(f.sortBy);
    }
  }, [location.state]);

  // ── OmniSearch keyboard shortcut ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOmniOpen(true); }
      if (e.key === 'Escape') setOmniOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus omni input when opened
  useEffect(() => {
    if (omniOpen) setTimeout(() => omniInputRef.current?.focus(), 80);
  }, [omniOpen]);

  // ── Filter Logic ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = products;
    if (searchQuery) filtered = filtered.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    if (selectedCategories.length > 0) {
      // Brand New / UK Used / Nigerian Used map to real per-product data via
      // `condition` — category_slug itself never holds these values today (see
      // note above), so matching only on category_slug would silently show zero
      // results for the categories that actually do have backing data.
      filtered = filtered.filter(p =>
        (p.category_slug && selectedCategories.includes(p.category_slug)) ||
        selectedCategories.some(slug => conditionForCategorySlug[slug] && p.condition === conditionForCategorySlug[slug]) ||
        selectedCategories.some(slug => booleanFlagForCategorySlug[slug]?.(p))
      );
    }
    if (selectedBrands.length > 0) filtered = filtered.filter(p => selectedBrands.some(b => p.brand?.toLowerCase() === b.toLowerCase()));
    if (selectedMemories.length > 0) {
      filtered = filtered.filter(product => {
        const storage = extractStorage(product);
        return storage && selectedMemories.includes(storage);
      });
    }
    if (selectedDeviceTypes.length > 0) {
      filtered = filtered.filter(product => {
        const catId = product.category_id || product.categoryId;
        if (!catId) return false;
        return selectedDeviceTypes.some(dt => {
          const dtId = dt.id || dt._id;
          return String(dtId) === String(catId);
        });
      });
    }
    filtered = filtered.filter(p => p.price >= priceRange.min && p.price <= priceRange.max);
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'name': return a.name.localeCompare(b.name);
        case 'rating': return (b.rating || 0) - (a.rating || 0);
        case 'newest': return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
        default: return 0;
      }
    });
    setFilteredProducts(sorted);
    setTotalProducts(sorted.length);
    setTotalPages(Math.ceil(sorted.length / itemsPerPage));
    setCurrentPage(1);
  }, [products, searchQuery, selectedCategories, selectedBrands, selectedMemories, selectedDeviceTypes, priceRange, sortBy]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSearchChange = async (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length > 2) {
      setShowSuggestions(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await searchAPI.getSearchSuggestions(value);
          if (response?.data) setSearchSuggestions(response.data.suggestions || response.data || []);
        } catch { setSearchSuggestions([]); }
      }, 300);
    } else {
      setShowSuggestions(false);
      setSearchSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setOmniOpen(false);
  };

  const handleSaveSearch = async () => {
    if (!searchQuery.trim()) { showCustomToast({ type: 'warning', title: 'No Search Query', message: 'Enter a search term to save' }); return; }
    try {
      await savedSearchesAPI.createSavedSearch({ query: searchQuery, filters: { categories: selectedCategories, brands: selectedBrands, memories: selectedMemories, deviceTypes: selectedDeviceTypes, priceRange, sortBy } });
      showCustomToast({ type: 'success', title: 'Search Saved', message: 'Your search has been saved' });
      const r = await savedSearchesAPI.getSavedSearches();
      if (r?.data) setSavedSearches(r.data);
    } catch (error) { if (!error.isAuthError) showCustomToast({ type: 'error', title: 'Save Failed', message: error.message || 'Failed to save search' }); }
  };

  const handleApplySavedSearch = (savedSearch) => {
    setSearchQuery(savedSearch.query || '');
    if (savedSearch.filters) {
      setSelectedCategories(savedSearch.filters.categories || []);
      setSelectedBrands(savedSearch.filters.brands || []);
      setSelectedMemories(savedSearch.filters.memories || []);
      setSelectedDeviceTypes(savedSearch.filters.deviceTypes || []);
      setPriceRange(savedSearch.filters.priceRange || actualPriceRange);
      setSortBy(savedSearch.filters.sortBy || 'featured');
    }
    showCustomToast({ type: 'success', title: 'Search Applied', message: `Applied: "${savedSearch.query}"` });
    setOmniOpen(false);
  };

  const handleDeleteSavedSearch = async (searchId, e) => {
    e.stopPropagation();
    try {
      await savedSearchesAPI.deleteSavedSearch(searchId);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId && s._id !== searchId));
      showCustomToast({ type: 'success', title: 'Deleted', message: 'Saved search removed' });
    } catch { showCustomToast({ type: 'error', title: 'Delete Failed', message: 'Failed to delete' }); }
  };

  const handleCategoryChange = (cat) => setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  const handleBrandChange = (brand) => setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]);
  const handleMemoryChange = (mem) => setSelectedMemories(prev => prev.includes(mem) ? prev.filter(m => m !== mem) : [...prev, mem]);
  const handleDeviceTypeChange = (dt) => {
    const dtId = dt.id || dt._id;
    setSelectedDeviceTypes(prev =>
      prev.some(d => (d.id || d._id) === dtId)
        ? prev.filter(d => (d.id || d._id) !== dtId)
        : [...prev, dt]
    );
  };

  const handleProductClick = (product) => navigate(`/product/${product._id || product.id}`);

  const handleWishlistToggle = async (product, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Sign Up Required', message: 'Create an account to save items to your wishlist.' });
      navigate('/auth');
      return;
    }
    const productId = product._id || product.id;
    try {
      if (wishlistItems.includes(productId)) {
        await wishlistAPI.removeFromWishlist(productId);
        setWishlistItems(prev => prev.filter(id => id !== productId));
        showCustomToast({ type: 'info', title: 'Removed from Wishlist', message: `${product.name} removed` });
      } else {
        await wishlistAPI.addToWishlist(productId);
        setWishlistItems(prev => [...prev, productId]);
        showCustomToast({ type: 'success', title: 'Added to Wishlist', message: `${product.name} added` });
      }
    } catch (error) { if (!error.isAuthError) showCustomToast({ type: 'error', title: 'Error', message: error.message || 'Failed to update wishlist' }); }
  };

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Sign Up Required', message: 'Create an account to add items to your cart.' });
      navigate('/auth');
      return;
    }
    const productId = product._id || product.id;
    if (!productId) { showCustomToast({ type: 'error', title: 'Error', message: 'Invalid product ID' }); return; }
    if (!product.stock || product.stock <= 0) { showCustomToast({ type: 'warning', title: 'Out of Stock', message: `${product.name} is unavailable` }); return; }

    // Reseller category restriction
    if (isReseller) {
      const restrictedConditions = ['nigerian_used', 'nigeria_used', 'for_sale', 'want_to_buy'];
      if (restrictedConditions.includes(product.condition)) {
        showCustomToast({
          type: 'warning',
          title: 'Purchase Restriction',
          message: 'Resellers are not permitted to purchase from this category.'
        });
        return;
      }
    }
    try {
      await addToCart(productId, 1);
      showCustomToast({ type: 'cart', title: 'Added to Cart!', message: `${product.name} added to cart` });
    } catch (error) { if (!error.isAuthError) showCustomToast({ type: 'error', title: 'Failed to Add', message: `Could not add: ${error.response?.data?.message || error.message}` }); }
  };

  const handleSwap = (product, e) => {
    e.stopPropagation();
    if (product?.stock > 0) {
      navigate('/swap-deals', { state: { selectedProduct: product, action: 'create_offer' } });
      showCustomToast({ type: 'success', title: 'Swap Initiated', message: `Prepare your swap for ${product.name}`, duration: 3000 });
    }
  };

  const handleBuyNow = (product, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Sign Up Required', message: 'Create an account to complete your purchase.' });
      navigate('/auth');
      return;
    }
    if (product?.stock > 0) {
      // Reseller category restriction
      if (isReseller) {
        const restrictedConditions = ['nigerian_used', 'nigeria_used', 'for_sale', 'want_to_buy'];
        if (restrictedConditions.includes(product.condition)) {
          showCustomToast({
            type: 'warning',
            title: 'Purchase Restriction',
            message: 'Resellers are not permitted to purchase from this category.'
          });
          return;
        }
      }
      // Sellers check out at the product's wholesale price — quote the same
      // figure the backend will charge, not the retail price.
      const unitPrice = getEffectivePrice(product, user);
      navigate('/checkout', { state: { items: [{ product_id: product._id || product.id, product, quantity: 1, price: unitPrice, name: product.title || product.name, image: product.images?.[0] || product.image }], total: unitPrice, buyNow: true, skipCart: true } });
      showCustomToast({ type: 'success', title: 'Quick Checkout', message: `Proceeding for ${product.name}`, duration: 2000 });
    }
  };

  const handlePriceRangeChange = (min, max) => setPriceRange({ min, max });
  const clearAllFilters = () => { setSelectedCategories([]); setSelectedBrands([]); setSelectedMemories([]); setSelectedDeviceTypes([]); setPriceRange({ min: actualPriceRange.min, max: actualPriceRange.max }); setSearchQuery(''); setBrandSearchQuery(''); };

  const formatCountdown = (expiresAt) => {
    const diff = new Date(expiresAt) - flashNow;
    if (diff <= 0) return 'Expired';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleCreateRequest = async () => {
    if (!requestForm.title.trim()) { showCustomToast({ type: 'warning', title: 'Required', message: 'Please enter a product title' }); return; }
    try {
      setSubmittingRequest(true);
      await requestsAPI.createRequest(requestForm);
      showCustomToast({ type: 'success', title: 'Request Submitted', message: 'Your product request has been posted' });
      setShowRequestModal(false);
      setRequestForm({ title: '', description: '', budget: '', category: '' });
      const r = await requestsAPI.getMyRequests();
      if (r?.data) setMyRequests(Array.isArray(r.data) ? r.data : r.data.requests || []);
    } catch (err) { showCustomToast({ type: 'error', title: 'Failed', message: err.message || 'Could not submit request' }); }
    finally { setSubmittingRequest(false); }
  };

  const handleDeleteRequest = async (id) => {
    try {
      await requestsAPI.deleteRequest(id);
      setMyRequests(prev => prev.filter(r => (r._id || r.id) !== id));
      showCustomToast({ type: 'success', title: 'Deleted', message: 'Request removed' });
    } catch { showCustomToast({ type: 'error', title: 'Error', message: 'Failed to delete request' }); }
  };

  const handleOpenRequest = async (req) => {
    setSelectedRequest(req);
    setRequestResponses([]);
    setRespondForm({ product_url: '', message: '' });
    setLoadingResponses(true);
    try {
      const res = await requestsAPI.getRequestResponses(req._id || req.id);
      const data = res?.data;
      setRequestResponses(Array.isArray(data) ? data : data?.responses || []);
    } catch { setRequestResponses([]); }
    finally { setLoadingResponses(false); }
  };

  const handleRespondToRequest = async () => {
    if (!respondForm.product_url.trim()) {
      showCustomToast({ type: 'warning', title: 'Required', message: 'Please enter a product URL' });
      return;
    }
    try {
      setSubmittingResponse(true);
      await requestsAPI.respondToRequest(selectedRequest._id || selectedRequest.id, respondForm);
      showCustomToast({ type: 'success', title: 'Response Sent', message: 'The buyer has been notified' });
      setRespondForm({ product_url: '', message: '' });
      // Refresh responses
      const res = await requestsAPI.getRequestResponses(selectedRequest._id || selectedRequest.id);
      const data = res?.data;
      setRequestResponses(Array.isArray(data) ? data : data?.responses || []);
      // Update response count on card
      const updatedReq = { ...selectedRequest, response_count: (selectedRequest.response_count || 0) + 1 };
      setSelectedRequest(updatedReq);
      setPublicRequests(prev => prev.map(r => (r._id || r.id) === (selectedRequest._id || selectedRequest.id) ? updatedReq : r));
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Failed', message: err?.response?.data?.message || 'Could not submit response' });
    } finally { setSubmittingResponse(false); }
  };

  const handleFulfillRequest = async () => {
    try {
      setFulfillingRequest(true);
      await requestsAPI.fulfillRequest(selectedRequest._id || selectedRequest.id);
      const updated = { ...selectedRequest, status: 'fulfilled', is_fulfilled: true };
      setSelectedRequest(updated);
      setMyRequests(prev => prev.map(r => (r._id || r.id) === (updated._id || updated.id) ? updated : r));
      setPublicRequests(prev => prev.map(r => (r._id || r.id) === (updated._id || updated.id) ? updated : r));
      showCustomToast({ type: 'success', title: 'Marked Fulfilled', message: 'Your request has been closed' });
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Error', message: err?.response?.data?.message || 'Failed to fulfill request' });
    } finally { setFulfillingRequest(false); }
  };
  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));
  const getActiveFiltersCount = () => selectedCategories.length + selectedBrands.length + selectedMemories.length + selectedDeviceTypes.length;
  const filteredBrands = brands.filter(b => b.toLowerCase().includes(brandSearchQuery.toLowerCase()));

  const getCurrentPageProducts = () => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  };

  const getPaginationPages = () => {
    const pages = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1);
    let start = Math.max(2, currentPage - 2);
    let end = Math.min(totalPages - 1, currentPage + 2);
    if (currentPage <= 3) end = Math.min(5, totalPages - 1);
    if (currentPage >= totalPages - 2) start = Math.max(2, totalPages - 4);
    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const currentProducts = getCurrentPageProducts();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`shop-root ${darkMode ? 'shop-dark' : ''}`}>
      {/* OmniSearch Overlay */}
      <AnimatePresence>
        {omniOpen && (
          <motion.div
            className="shop-omni-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setOmniOpen(false)}
          >
            <motion.div
              className="shop-omni-panel"
              initial={{ scale: 0.96, y: -16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: -16, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 36 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="shop-omni-input-row">
                <i className="fas fa-search shop-omni-search-icon" />
                <input
                  ref={omniInputRef}
                  className="shop-omni-input"
                  placeholder="Search products, brands, categories..."
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setOmniOpen(false); }}
                />
                {searchQuery && (
                  <>
                    <button className="shop-omni-save-btn" onClick={handleSaveSearch} title="Save search">
                      <i className="fas fa-bookmark" />
                    </button>
                    <button className="shop-omni-clear-btn" onClick={() => { setSearchQuery(''); setSearchSuggestions([]); }} title="Clear">
                      <i className="fas fa-times" />
                    </button>
                  </>
                )}
                <kbd className="shop-omni-esc" onClick={() => setOmniOpen(false)}>ESC</kbd>
              </div>

              {/* Suggestions */}
              {(searchSuggestions.length > 0 || (!searchQuery && savedSearches.length > 0)) && (
                <div className="shop-omni-results">
                  {!searchQuery && savedSearches.length > 0 && (
                    <div className="shop-omni-section">
                      <div className="shop-omni-section-title">
                        <i className="fas fa-history" /> Recent Searches
                      </div>
                      {savedSearches.slice(0, 5).map(s => (
                        <div key={s._id || s.id} className="shop-omni-item" onClick={() => handleApplySavedSearch(s)}>
                          <i className="fas fa-bookmark shop-omni-item-icon" />
                          <span>{s.query}</span>
                          <button className="shop-omni-item-del" onClick={(e) => handleDeleteSavedSearch(s._id || s.id, e)}>
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchSuggestions.length > 0 && (
                    <div className="shop-omni-section">
                      <div className="shop-omni-section-title">
                        <i className="fas fa-search" /> Suggestions
                      </div>
                      {searchSuggestions.map((s, i) => (
                        <div key={i} className="shop-omni-item" onClick={() => handleSuggestionClick(s)}>
                          <i className="fas fa-arrow-right shop-omni-item-icon" />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {searchQuery && filteredProducts.length > 0 && (
                <div className="shop-omni-footer">
                  <span>{filteredProducts.length} results for "<strong>{searchQuery}</strong>"</span>
                  <button className="shop-omni-view-all" onClick={() => setOmniOpen(false)}>
                    View all <i className="fas fa-arrow-right" />
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="shop-container">
        {/* Breadcrumb */}
        <div className="shop-breadcrumb">
          <a href="/homepage">Home</a>
          <i className="fas fa-chevron-right" />
          <span>Shop</span>
        </div>

        {wantToBuyMode && (
          <div className="shop-wtb-view">
            <div className="shop-wtb-header">
              <div className="shop-wtb-header__icon"><i className="fas fa-search-plus" /></div>
              <div>
                <h2 className="shop-wtb-header__title">Want to Buy</h2>
                <p className="shop-wtb-header__sub">
                  {isSeller
                    ? 'Browse product requests from buyers. Respond with your available listings.'
                    : 'Post a request for a product you\'re looking for. Sellers will respond with availability and pricing.'}
                </p>
              </div>
              {user && !isSeller && (
                <button className="shop-requests__new-btn" onClick={() => setShowRequestModal(true)}>
                  <i className="fas fa-plus" /> Post Request
                </button>
              )}
            </div>

            {/* Sellers: browse all public buyer requests */}
            {isSeller && (
              <>
                {publicRequests.length > 0 ? (
                  <div className="shop-requests__grid">
                    {publicRequests.map(req => (
                      <div key={req._id || req.id} className="shop-request-card shop-request-card--clickable" onClick={() => handleOpenRequest(req)}>
                        <div className="shop-request-card__top">
                          <span className="shop-request-card__title">{req.title}</span>
                          <span className={`shop-request-card__status shop-request-card__status--${req.status || 'open'}`}>
                            {req.status || 'open'}
                          </span>
                        </div>
                        {req.description && <p className="shop-request-card__desc">{req.description}</p>}
                        <div className="shop-request-card__meta">
                          {req.budget && <span><i className="fas fa-tag" /> ₦{Number(req.budget).toLocaleString()}</span>}
                          <span><i className="fas fa-comment-dots" /> {req.response_count || 0} responses</span>
                        </div>
                        <div className="shop-request-card__view-hint"><i className="fas fa-eye" /> View &amp; Respond</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="shop-requests__empty">
                    <i className="fas fa-search" />
                    <p>No open buyer requests at the moment. Check back soon!</p>
                  </div>
                )}
              </>
            )}

            {/* Buyers: only show their own requests */}
            {!isSeller && (
              <>
                {myRequests.length > 0 ? (
                  <div className="shop-requests__mine">
                    <h4 className="shop-requests__mine-title">My Requests</h4>
                    <div className="shop-requests__grid">
                      {myRequests.map(req => (
                        <div key={req._id || req.id} className="shop-request-card shop-request-card--mine shop-request-card--clickable" onClick={() => handleOpenRequest(req)}>
                          <div className="shop-request-card__top">
                            <span className="shop-request-card__title">{req.title}</span>
                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                              <span className={`shop-request-card__status shop-request-card__status--${req.status || 'open'}`}>
                                {req.status || 'open'}
                              </span>
                              <button className="shop-request-card__del" onClick={e => { e.stopPropagation(); handleDeleteRequest(req._id || req.id); }}>
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                          {req.description && <p className="shop-request-card__desc">{req.description}</p>}
                          <div className="shop-request-card__meta">
                            {req.budget && <span><i className="fas fa-tag" /> ₦{Number(req.budget).toLocaleString()}</span>}
                            <span><i className="fas fa-comment-dots" /> {req.response_count || 0} responses</span>
                          </div>
                          <div className="shop-request-card__view-hint"><i className="fas fa-eye" /> View Responses</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="shop-requests__empty">
                    <i className="fas fa-search" />
                    {user
                      ? <p>You haven't posted any requests yet. Click <strong>Post Request</strong> above to get started!</p>
                      : <p>No requests found. <a href="/auth">Sign in</a> to post your first product request.</p>
                    }
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!wantToBuyMode && (<>
        {/* OmniSearch Trigger */}
        <div className="shop-omni-trigger" onClick={() => setOmniOpen(true)}>
          <i className="fas fa-search shop-omni-trigger-icon" />
          <span className="shop-omni-trigger-text">
            {searchQuery || 'Search for products, brands...'}
          </span>
          <div className="shop-omni-trigger-right">
            {searchQuery && (
              <span className="shop-omni-active-badge">{filteredProducts.length} results</span>
            )}
            <kbd className="shop-omni-hotkey">⌘K</kbd>
          </div>
        </div>

        {/* Results Bar */}
        <div className="shop-results-bar">
          <div className="shop-results-info">
            <span className="shop-results-count">{totalProducts} products</span>
            {getActiveFiltersCount() > 0 && (
              <button className="shop-clear-filters" onClick={clearAllFilters}>
                <i className="fas fa-times" /> Clear ({getActiveFiltersCount()})
              </button>
            )}
          </div>
          <div className="shop-results-controls">
            <button className="shop-mobile-filter-btn" onClick={() => setMobileFiltersOpen(true)}>
              <i className="fas fa-sliders-h" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="shop-filter-badge">{getActiveFiltersCount()}</span>
              )}
            </button>
            <div className="shop-view-toggle">
              <button className={`shop-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid view">
                <i className="fas fa-th-large" />
              </button>
              <button className={`shop-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List view">
                <i className="fas fa-list" />
              </button>
            </div>
            <select className="shop-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low → High</option>
              <option value="price-high">Price: High → Low</option>
              <option value="name">Name A–Z</option>
              <option value="rating">Top Rated</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {/* Layout */}
        <div className="shop-layout">
          {/* Sidebar */}
          <AnimatePresence>
            {(mobileFiltersOpen || true) && (
              <aside className={`shop-sidebar ${mobileFiltersOpen ? 'shop-sidebar--open' : ''}`}>
                <div className="shop-sidebar-header">
                  <span>Explore</span>
                  <button className="shop-sidebar-close" onClick={() => setMobileFiltersOpen(false)}>
                    <i className="fas fa-times" />
                  </button>
                </div>

                {/* Category */}
                <div className="shop-filter-block">
                  <button className="shop-filter-toggle" onClick={() => toggleSection('category')}>
                    <span>
                      Category
                      {selectedCategories.length > 0 && <em className="shop-filter-count">{selectedCategories.length}</em>}
                    </span>
                    <i className={`fas fa-chevron-${expandedSections.category ? 'up' : 'down'}`} />
                  </button>
                  {expandedSections.category && (
                    <div className="shop-filter-list">
                      {(categories.length > 0 ? categories : fallbackCategories).map(cat => (
                        <label key={cat.id || cat.value} className="shop-filter-check">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(cat.id || cat.value)}
                            onChange={() => handleCategoryChange(cat.id || cat.value)}
                          />
                          <span className="shop-check-box" />
                          <span className="shop-check-label">{cat.name || cat.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Device Types */}
                <div className="shop-filter-block">
                  <button className="shop-filter-toggle" onClick={() => toggleSection('deviceTypes')}>
                    <span>
                      Device Type
                      {selectedDeviceTypes.length > 0 && <em className="shop-filter-count">{selectedDeviceTypes.length}</em>}
                    </span>
                    <i className={`fas fa-chevron-${expandedSections.deviceTypes ? 'up' : 'down'}`} />
                  </button>
                  {expandedSections.deviceTypes && (
                    <div className="shop-filter-list">
                      {deviceTypes.map(dt => {
                        const dtId = dt.id || dt._id || dt;
                        const dtName = dt.name || dt;
                        const isChecked = selectedDeviceTypes.some(d => (d.id || d._id || d) === dtId);
                        return (
                          <label key={dtId} className="shop-filter-check">
                            <input type="checkbox" checked={isChecked} onChange={() => handleDeviceTypeChange(dt)} />
                            <span className="shop-check-box" />
                            <span className="shop-check-label">{dtName}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="shop-filter-block">
                  <button className="shop-filter-toggle" onClick={() => toggleSection('price')}>
                    <span>Price Range</span>
                    <i className={`fas fa-chevron-${expandedSections.price ? 'up' : 'down'}`} />
                  </button>
                  {expandedSections.price && (
                    <div className="shop-price-block">
                      <input type="range" min={actualPriceRange.min} max={actualPriceRange.max} value={priceRange.min}
                        onChange={e => handlePriceRangeChange(parseInt(e.target.value), priceRange.max)} className="shop-range" />
                      <input type="range" min={actualPriceRange.min} max={actualPriceRange.max} value={priceRange.max}
                        onChange={e => handlePriceRangeChange(priceRange.min, parseInt(e.target.value))} className="shop-range" />
                      <div className="shop-price-labels">
                        <span>₦{(priceRange.min / 1000).toFixed(0)}k</span>
                        <span>₦{priceRange.max >= 1000000 ? (priceRange.max / 1000000).toFixed(1) + 'm' : (priceRange.max / 1000).toFixed(0) + 'k'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Brands */}
                <div className="shop-filter-block">
                  <button className="shop-filter-toggle" onClick={() => toggleSection('brands')}>
                    <span>
                      Brand
                      {selectedBrands.length > 0 && <em className="shop-filter-count">{selectedBrands.length}</em>}
                    </span>
                    <i className={`fas fa-chevron-${expandedSections.brands ? 'up' : 'down'}`} />
                  </button>
                  {expandedSections.brands && (
                    <>
                      <div className="shop-brand-search">
                        <i className="fas fa-search" />
                        <input type="text" placeholder="Search brands" value={brandSearchQuery} onChange={e => setBrandSearchQuery(e.target.value)} />
                      </div>
                      <div className="shop-filter-list">
                        {filteredBrands.map(brand => (
                          <label key={brand} className="shop-filter-check">
                            <input type="checkbox" checked={selectedBrands.includes(brand)} onChange={() => handleBrandChange(brand)} />
                            <span className="shop-check-box" />
                            <span className="shop-check-label">{brand}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Memory — only shown when at least one product has a detectable storage size */}
                {memories.length > 0 && (
                  <div className="shop-filter-block">
                    <button className="shop-filter-toggle" onClick={() => toggleSection('memory')}>
                      <span>
                        Storage
                        {selectedMemories.length > 0 && <em className="shop-filter-count">{selectedMemories.length}</em>}
                      </span>
                      <i className={`fas fa-chevron-${expandedSections.memory ? 'up' : 'down'}`} />
                    </button>
                    {expandedSections.memory && (
                      <div className="shop-filter-list">
                        {memories.map(mem => (
                          <label key={mem} className="shop-filter-check">
                            <input type="checkbox" checked={selectedMemories.includes(mem)} onChange={() => handleMemoryChange(mem)} />
                            <span className="shop-check-box" />
                            <span className="shop-check-label">{mem}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Saved Searches */}
                {savedSearches.length > 0 && (
                  <div className="shop-filter-block">
                    <button className="shop-filter-toggle" onClick={() => toggleSection('savedSearches')}>
                      <span>
                        Saved Searches
                        <em className="shop-filter-count">{savedSearches.length}</em>
                      </span>
                      <i className={`fas fa-chevron-${expandedSections.savedSearches ? 'up' : 'down'}`} />
                    </button>
                    {expandedSections.savedSearches && (
                      <div className="shop-filter-list">
                        {savedSearches.map(s => (
                          <div key={s._id || s.id} className="shop-saved-search-item" onClick={() => handleApplySavedSearch(s)}>
                            <i className="fas fa-search shop-saved-search-icon" />
                            <span className="shop-saved-search-label">{s.query}</span>
                            <button className="shop-saved-search-del" onClick={e => handleDeleteSavedSearch(s._id || s.id, e)}>
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </aside>
            )}
          </AnimatePresence>

          {/* Mobile overlay */}
          {mobileFiltersOpen && (
            <motion.div
              className="shop-sidebar-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileFiltersOpen(false)}
            />
          )}

          {/* Products Area */}
          <div className="shop-products-area">

            {/* ── Flash Deals ── */}
            {(loadingFlash || flashDeals.length > 0) && (
              <section className="shop-flash">
                <div className="shop-flash__header">
                  <span className="shop-flash__title"><i className="fas fa-fire" /> Flash Deals</span>
                  {flashDeals.length > 0 && <span className="shop-flash__badge">{flashDeals.length} active</span>}
                </div>
                {loadingFlash ? (
                  <div className="shop-flash__loading"><i className="fas fa-spinner fa-spin" /> Loading deals...</div>
                ) : (
                  <div className="shop-flash__strip">
                    {flashDeals.map(prod => {
                      const img = prod.images?.[0]?.url || (typeof prod.images?.[0] === 'string' ? prod.images[0] : null) || prod.image;
                      const name = prod.title || prod.name || 'Product';
                      const price = prod.flash_deal_price || prod.price;
                      const origPrice = prod.price;
                      const discount = origPrice && price && origPrice > price
                        ? Math.round((1 - price / origPrice) * 100)
                        : null;
                      const expires = prod.flash_deal_ends_at;
                      const pid = prod._id || prod.id;
                      return (
                        <motion.div
                          key={pid}
                          className="shop-flash-card"
                          whileHover={{ y: -4 }}
                          onClick={() => navigate(`/product/${pid}`)}
                        >
                          <div className="shop-flash-card__img-wrap">
                            {img
                              ? <img src={img} alt={name} className="shop-flash-card__img" loading="lazy" />
                              : <div className="shop-flash-card__img-ph"><i className="fas fa-box" /></div>}
                            {discount > 0 && <span className="shop-flash-card__discount">-{discount}%</span>}
                            {prod.is_early_access && <span className="shop-flash-card__discount" style={{ left: '0.5rem', right: 'auto', background: '#F59E0B' }}>EARLY ACCESS</span>}
                          </div>
                          <div className="shop-flash-card__body">
                            <p className="shop-flash-card__name">{name}</p>
                            <div className="shop-flash-card__prices">
                              <span className="shop-flash-card__price">₦{Number(price || 0).toLocaleString()}</span>
                              {origPrice > price && <span className="shop-flash-card__orig">₦{Number(origPrice).toLocaleString()}</span>}
                            </div>
                            {expires && (
                              <div className="shop-flash-card__timer">
                                <i className="fas fa-clock" /> {formatCountdown(expires)}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {loading ? (
              <div className={`shop-grid ${viewMode === 'list' ? 'shop-grid--list' : ''}`}>
                {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : currentProducts.length > 0 ? (
              <motion.div
                className={`shop-grid ${viewMode === 'list' ? 'shop-grid--list' : ''}`}
                layout
              >
                <AnimatePresence mode="popLayout">
                  {currentProducts.map((product, idx) => {
                    const productId = product._id || product.id;
                    const inWishlist = wishlistItems.includes(productId);
                    const isOOS = !product.stock || product.stock <= 0;

                    return (
                      <TiltCard
                        key={productId}
                        className={`shop-card ${isOOS ? 'shop-card--oos' : ''}`}
                        onClick={() => handleProductClick(product)}
                      >
                        {/* Image */}
                        <div className="shop-card__img-wrap">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="shop-card__img"
                            loading="lazy"
                          />
                          {/* OOS frost */}
                          {isOOS && <div className="shop-card__frost"><span>Out of Stock</span></div>}

                          {/* Condition badge */}
                          <ConditionBadge condition={product.condition} />

                          {/* New badge */}
                          {product.isNew && !isOOS && <span className="shop-card__new-badge">NEW</span>}

                          {/* Admin status badges */}
                          {(product.is_good_deal || product.is_trending || product.is_hot || product.is_featured) && (
                            <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 3, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                              {product.is_good_deal && (
                                <span style={{ padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.64rem', fontWeight: '700', background: 'linear-gradient(135deg,#10B981,#059669)', color: 'white', boxShadow: '0 2px 5px rgba(16,185,129,0.35)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                                  <i className="fas fa-tag" style={{ fontSize: '0.58rem' }} /> Good Deal
                                </span>
                              )}
                              {product.is_trending && (
                                <span style={{ padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.64rem', fontWeight: '700', background: 'linear-gradient(135deg,#F59E0B,#D97706)', color: 'white', boxShadow: '0 2px 5px rgba(245,158,11,0.35)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                                  <i className="fas fa-chart-line" style={{ fontSize: '0.58rem' }} /> Trending
                                </span>
                              )}
                              {product.is_hot && (
                                <span style={{ padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.64rem', fontWeight: '700', background: 'linear-gradient(135deg,#EF4444,#DC2626)', color: 'white', boxShadow: '0 2px 5px rgba(239,68,68,0.35)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                                  <i className="fas fa-fire" style={{ fontSize: '0.58rem' }} /> Hot
                                </span>
                              )}
                              {product.is_featured && (
                                <span style={{ padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.64rem', fontWeight: '700', background: 'linear-gradient(135deg,#22C55E,#16A34A)', color: 'white', boxShadow: '0 2px 5px rgba(34,197,94,0.35)', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                                  <i className="fas fa-star" style={{ fontSize: '0.58rem' }} /> Featured
                                </span>
                              )}
                            </div>
                          )}

                          {/* Wishlist */}
                          <button
                            className={`shop-card__wish-btn ${inWishlist ? 'shop-card__wish-btn--active' : ''}`}
                            onClick={e => handleWishlistToggle(product, e)}
                            title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                          >
                            <i className={`${inWishlist ? 'fas' : 'far'} fa-heart`} />
                          </button>
                        </div>

                        {/* Info */}
                        <div className="shop-card__body">
                          <h3 className="shop-card__name">{product.name}</h3>

                          {product.brand && (
                            <span className="shop-card__brand">{product.brand}</span>
                          )}

                          <PriceTag
                            price={product.price}
                            sellerPrice={product.seller_price || product.sellerPrice}
                            isSeller={isSeller}
                          />

                          {product.stock > 0 && (
                            <span className="shop-card__stock">{product.stock} in stock</span>
                          )}

                          {/* Seller info with identity masking */}
                          <div className="AutoboyExpress-seller-info-modern">
                            <span className="AutoboyExpress-seller-name-label">
                              {product.seller_premium
                                ? (product.seller_name || product.seller?.name || 'Seller')
                                : `Seller ${String(product.seller_id || '000').slice(-3).padStart(3, '0')}`
                              }
                            </span>
                            {product.seller_premium && (
                              <span className="AutoboyExpress-product-premium-badge">
                                <i className="fas fa-crown"></i> Premium
                              </span>
                            )}
                            {product.seller_verified && (
                              <span className="AutoboyExpress-product-verified-badge">
                                <i className="fas fa-check"></i> Verified
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="shop-card__actions">
                            <button
                              className="shop-card__action-btn shop-card__view-btn"
                              onClick={e => { e.stopPropagation(); handleProductClick(product); }}
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                            <button
                              className={`shop-card__action-btn shop-card__swap-btn ${isOOS ? 'shop-card__action-btn--disabled' : ''}`}
                              onClick={e => handleSwap(product, e)}
                              disabled={isOOS}
                              title="Swap"
                            >
                              <i className="fas fa-exchange-alt" />
                            </button>
                            <button
                              className={`shop-card__action-btn shop-card__cart-btn ${isOOS ? 'shop-card__action-btn--disabled' : ''}`}
                              onClick={e => handleAddToCart(product, e)}
                              disabled={isOOS}
                              title="Add to cart"
                            >
                              <i className="fas fa-shopping-cart" />
                            </button>
                            {!isOOS && (
                              <button
                                className="shop-card__action-btn shop-card__buy-btn"
                                onClick={e => handleBuyNow(product, e)}
                                title="Buy now"
                              >
                                <i className="fas fa-bolt" />
                              </button>
                            )}
                          </div>
                        </div>
                      </TiltCard>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            ) : (
              <div className="shop-empty">
                <svg viewBox="0 0 200 160" className="shop-empty__svg">
                  <motion.circle cx="100" cy="70" r="50" fill="none" stroke="#22C55E" strokeWidth="3" strokeDasharray="8 4"
                    animate={{ rotate: 360 }} transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                    style={{ transformOrigin: '100px 70px' }} />
                  <motion.path d="M76 76 L100 52 L124 76 M100 52 L100 100" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round"
                    animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} />
                  <text x="100" y="140" textAnchor="middle" fill="#94A3B8" fontSize="14">No products found</text>
                </svg>
                <p>Try adjusting your search or filters</p>
                {getActiveFiltersCount() > 0 && (
                  <button className="shop-empty__btn" onClick={clearAllFilters}>Clear All Filters</button>
                )}
              </div>
            )}

            {/* Pagination Pill */}
            {totalPages > 1 && (
              <div className="shop-pagination">
                <button
                  className="shop-pg-arrow"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left" />
                </button>

                <div className="shop-pg-pill">
                  {getPaginationPages().map((page, i) =>
                    page === '...' ? (
                      <span key={`e${i}`} className="shop-pg-ellipsis">…</span>
                    ) : (
                      <motion.button
                        key={page}
                        className={`shop-pg-dot ${currentPage === page ? 'shop-pg-dot--active' : ''}`}
                        onClick={() => handlePageChange(page)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {page}
                      </motion.button>
                    )
                  )}
                </div>

                <button
                  className="shop-pg-arrow"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </div>
            )}
            {/* ── Product Requests ── */}
            <section className="shop-requests">
              <div className="shop-requests__header">
                <div>
                  <h3 className="shop-requests__title"><i className="fas fa-search-plus" /> Can't find it? Request it</h3>
                  <p className="shop-requests__sub">Post a product request and sellers will respond with availability</p>
                </div>
                {user && (
                  <button className="shop-requests__new-btn" onClick={() => setShowRequestModal(true)}>
                    <i className="fas fa-plus" /> New Request
                  </button>
                )}
              </div>

              {publicRequests.length > 0 && (
                <div className="shop-requests__grid">
                  {publicRequests.map(req => (
                    <div key={req._id || req.id} className="shop-request-card shop-request-card--clickable" onClick={() => handleOpenRequest(req)}>
                      <div className="shop-request-card__top">
                        <span className="shop-request-card__title">{req.title}</span>
                        <span className={`shop-request-card__status shop-request-card__status--${req.status || 'open'}`}>
                          {req.status || 'open'}
                        </span>
                      </div>
                      {req.description && <p className="shop-request-card__desc">{req.description}</p>}
                      <div className="shop-request-card__meta">
                        {req.budget && <span><i className="fas fa-tag" /> ₦{Number(req.budget).toLocaleString()}</span>}
                        <span><i className="fas fa-comment-dots" /> {req.response_count || 0} responses</span>
                      </div>
                      <div className="shop-request-card__view-hint"><i className="fas fa-eye" /> View &amp; Respond</div>
                    </div>
                  ))}
                </div>
              )}

              {myRequests.length > 0 && (
                <div className="shop-requests__mine">
                  <h4 className="shop-requests__mine-title">My Requests</h4>
                  <div className="shop-requests__grid">
                    {myRequests.map(req => (
                      <div key={req._id || req.id} className="shop-request-card shop-request-card--mine shop-request-card--clickable" onClick={() => handleOpenRequest(req)}>
                        <div className="shop-request-card__top">
                          <span className="shop-request-card__title">{req.title}</span>
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <span className={`shop-request-card__status shop-request-card__status--${req.status || 'open'}`}>
                              {req.status || 'open'}
                            </span>
                            <button className="shop-request-card__del" onClick={e => { e.stopPropagation(); handleDeleteRequest(req._id || req.id); }}>
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </div>
                        {req.description && <p className="shop-request-card__desc">{req.description}</p>}
                        <div className="shop-request-card__meta">
                          {req.budget && <span><i className="fas fa-tag" /> ₦{Number(req.budget).toLocaleString()}</span>}
                          <span><i className="fas fa-comment-dots" /> {req.response_count || 0} responses</span>
                        </div>
                        <div className="shop-request-card__view-hint"><i className="fas fa-eye" /> View Responses</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {publicRequests.length === 0 && myRequests.length === 0 && !user && (
                <div className="shop-requests__empty">
                  <i className="fas fa-search" />
                  <p>No product requests yet. <a href="/login">Sign in</a> to post your first request.</p>
                </div>
              )}
            </section>

          </div>
        </div>
        </>)}
      </div>

      {/* ── Request Detail Modal ── */}
      <AnimatePresence>
        {selectedRequest && (
          <motion.div
            className="shop-omni-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setSelectedRequest(null)}
          >
            <motion.div
              className="shop-req-detail-modal"
              initial={{ scale: 0.96, y: -16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: -16, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 36 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="shop-req-detail__header">
                <div className="shop-req-detail__header-left">
                  <span className={`shop-request-card__status shop-request-card__status--${selectedRequest.status || 'open'}`}>
                    {selectedRequest.status || 'open'}
                  </span>
                  <h3 className="shop-req-detail__title">{selectedRequest.title}</h3>
                </div>
                <button className="shop-omni-esc" onClick={() => setSelectedRequest(null)}>✕</button>
              </div>

              <div className="shop-req-detail__body">
                {/* Request info */}
                {selectedRequest.description && (
                  <p className="shop-req-detail__desc">{selectedRequest.description}</p>
                )}
                <div className="shop-req-detail__meta">
                  {(selectedRequest.budget_min || selectedRequest.budget_max || selectedRequest.budget) && (
                    <span><i className="fas fa-tag" />
                      {selectedRequest.budget_min && selectedRequest.budget_max
                        ? `₦${Number(selectedRequest.budget_min).toLocaleString()} – ₦${Number(selectedRequest.budget_max).toLocaleString()}`
                        : `₦${Number(selectedRequest.budget_max || selectedRequest.budget_min || selectedRequest.budget).toLocaleString()}`
                      }
                    </span>
                  )}
                  {selectedRequest.location_preference && (
                    <span><i className="fas fa-map-marker-alt" /> {selectedRequest.location_preference}</span>
                  )}
                  {selectedRequest.condition_preference && (
                    <span><i className="fas fa-certificate" /> {selectedRequest.condition_preference.replace(/_/g, ' ')}</span>
                  )}
                  {selectedRequest.urgency && (
                    <span><i className="fas fa-clock" /> {selectedRequest.urgency.replace(/_/g, ' ')}</span>
                  )}
                  {selectedRequest.category && (
                    <span><i className="fas fa-th-large" /> {selectedRequest.category}</span>
                  )}
                </div>

                {/* Fulfill button for request owner */}
                {user && (user._id || user.id) === selectedRequest.user_id && selectedRequest.status !== 'fulfilled' && (
                  <button
                    className="shop-req-detail__fulfill-btn"
                    onClick={handleFulfillRequest}
                    disabled={fulfillingRequest}
                  >
                    {fulfillingRequest
                      ? <><i className="fas fa-spinner fa-spin" /> Marking...</>
                      : <><i className="fas fa-check-circle" /> Mark as Fulfilled</>}
                  </button>
                )}

                {/* Responses section */}
                <div className="shop-req-detail__responses">
                  <h4 className="shop-req-detail__responses-title">
                    <i className="fas fa-reply" /> Seller Responses
                    {!loadingResponses && <span className="shop-req-detail__resp-count">{requestResponses.length}</span>}
                  </h4>

                  {loadingResponses ? (
                    <div className="shop-req-detail__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading responses...
                    </div>
                  ) : requestResponses.length > 0 ? (
                    <div className="shop-req-detail__resp-list">
                      {requestResponses.map((resp, i) => (
                        <div key={resp._id || resp.id || i} className="shop-req-detail__resp-item">
                          <div className="shop-req-detail__resp-top">
                            <span className="shop-req-detail__resp-seller">
                              <i className="fas fa-store" /> {resp.seller_name || resp.seller?.username || 'Seller'}
                            </span>
                            <span className="shop-req-detail__resp-time">
                              {resp.created_at ? new Date(resp.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' }) : ''}
                            </span>
                          </div>
                          {resp.message && <p className="shop-req-detail__resp-msg">{resp.message}</p>}
                          {resp.product_url && (
                            <a
                              href={resp.product_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shop-req-detail__resp-link"
                            >
                              <i className="fas fa-external-link-alt" /> View Product
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="shop-req-detail__resp-empty">No responses yet. {isSeller ? 'Be the first to respond!' : 'Sellers will respond soon.'}</p>
                  )}
                </div>

                {/* Seller respond form */}
                {isSeller && selectedRequest.status !== 'fulfilled' && (
                  <div className="shop-req-detail__respond-form">
                    <h4 className="shop-req-detail__respond-title"><i className="fas fa-paper-plane" /> Respond to this Request</h4>
                    <div className="shop-req-modal__field">
                      <label>Product URL <span style={{ color: '#EF4444' }}>*</span></label>
                      <input
                        placeholder="https://autoboyexpress.com/product/..."
                        value={respondForm.product_url}
                        onChange={e => setRespondForm(f => ({ ...f, product_url: e.target.value }))}
                      />
                    </div>
                    <div className="shop-req-modal__field">
                      <label>Message (optional)</label>
                      <textarea
                        rows="2"
                        placeholder="Tell the buyer about your item..."
                        value={respondForm.message}
                        onChange={e => setRespondForm(f => ({ ...f, message: e.target.value }))}
                      />
                    </div>
                    <button
                      className="shop-req-modal__submit"
                      onClick={handleRespondToRequest}
                      disabled={submittingResponse}
                    >
                      {submittingResponse
                        ? <><i className="fas fa-spinner fa-spin" /> Sending...</>
                        : <><i className="fas fa-paper-plane" /> Send Response</>}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Product Request Modal ── */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            className="shop-omni-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setShowRequestModal(false)}
          >
            <motion.div
              className="shop-req-modal"
              initial={{ scale: 0.96, y: -16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: -16, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 36 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="shop-req-modal__header">
                <h3><i className="fas fa-search-plus" /> Request a Product</h3>
                <button className="shop-omni-esc" onClick={() => setShowRequestModal(false)}>✕</button>
              </div>
              <div className="shop-req-modal__body">
                <div className="shop-req-modal__field">
                  <label>Product Title <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    placeholder="e.g. iPhone 15 Pro Max 256GB Blue"
                    value={requestForm.title}
                    onChange={e => setRequestForm(f => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="shop-req-modal__field">
                  <label>Description</label>
                  <textarea
                    rows="2"
                    placeholder="Specific requirements, condition, accessories..."
                    value={requestForm.description}
                    onChange={e => setRequestForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="shop-req-modal__row">
                  <div className="shop-req-modal__field">
                    <label>Budget (₦)</label>
                    <input
                      type="number" min="0"
                      placeholder="Max you'll pay"
                      value={requestForm.budget}
                      onChange={e => setRequestForm(f => ({ ...f, budget: e.target.value }))}
                    />
                  </div>
                  <div className="shop-req-modal__field">
                    <label>Category</label>
                    <select
                      value={requestForm.category}
                      onChange={e => setRequestForm(f => ({ ...f, category: e.target.value }))}
                    >
                      <option value="">Select a category</option>
                      {deviceTypes.map(dt => {
                        const dtId = dt.id || dt._id || dt;
                        const dtName = dt.name || dt;
                        return <option key={dtId} value={dtName}>{dtName}</option>;
                      })}
                    </select>
                  </div>
                </div>
              </div>
              <div className="shop-req-modal__footer">
                <button className="shop-omni-view-all" onClick={() => setShowRequestModal(false)}>Cancel</button>
                <button
                  className="shop-req-modal__submit"
                  onClick={handleCreateRequest}
                  disabled={submittingRequest}
                >
                  {submittingRequest
                    ? <><i className="fas fa-spinner fa-spin" /> Submitting...</>
                    : <><i className="fas fa-paper-plane" /> Submit Request</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Shop;
